import { cellCenter } from '../grid.ts';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, EmberRules, EmberState, PulseSpot, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { shootTick } from './workshop.ts';

/**
 * The river of embers (docs/sendas/artifice.md, chapter 3, act 2, «El río de brasa»; the ground in
 * battle/corazon-layout.ts). A cavern with a river of magma along its north; on the bank the old dwarven trip-hammer,
 * a great hammer worked by a wheel the river's heat turns, and beside it the heart stone set in the rock. Someone
 * tried to free the hammer and left it half done (the clan's tools lie about). Dunna frees it for `work` while the
 * nightmares go for her (out of the river and the two tunnels: they hunt by the common rule); a blow stops her a
 * moment. The magma rises in pulses (`pulse`): spots of the floor glow red `warn` (3 s) before they burst, the first
 * on the hero, the rest about the bank (never on Dunna's place); whoever is on one as it bursts is struck, nightmares
 * too. Won with the hammer free; lost with Dunna or the hero down.
 *
 * Cost: a handful of spots a pulse; the nightmares are looked at only as a spot bursts.
 */

const fresh = (r: EmberRules): EmberState => ({ prog: 0, pulse: r.pulse.first, spots: [], foe: r.foes.first, foes: 0, pulses: 0, end: -1, shot: 0 });

/** Bursts what is due: the hero, Dunna and the nightmares on each spot. */
export const pulseLand = (s: BattleState, spots: PulseSpot[], r: { r: number; heroBp: number; blows: number }, foeBp = 2500): PulseSpot[] => {
  let k = 0;
  for (const p of spots) {
    if (p.t > s.tick) {
      spots[k++] = p;
      continue;
    }
    ev(s, { t: 'senda', kind: 'pulse', npc: '', x: p.x, y: p.y });
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, p.x, p.y, r.r)) damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'fire', true);
    for (const n of s.senda?.npcs ?? []) {
      if (n.hp <= 0 || n.away || !withinRange(n.x, n.y, p.x, p.y, r.r)) continue;
      n.hp = imax(0, n.hp - r.blows);
      ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    }
    for (const e of s.enemies) if (e.hp > 0 && withinRange(e.x, e.y, p.x, p.y, r.r)) damageEnemy(s, e, imax(1, mulBp(e.maxHp, foeBp)), { pid: '', pierce: true });
  }
  spots.length = k;
  return spots;
};

/** A pulse: its spots marked, the first on the hero (a little off), the rest on floor cells away from it. */
const pulse = (s: BattleState, r: EmberRules, w: EmberState, cue: (c: string) => void): void => {
  if (s.waveT < w.pulse || w.end >= 0) return;
  w.pulse = s.waveT + r.pulse.every;
  const P = r.pulse;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const t = s.tick + P.warn;
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: P.warn });
  };
  // (Never on Dunna: a spot on the hero by her goes elsewhere.)
  const clear = (x: number, y: number): boolean => !withinRange(x, y, r.dunnaAt.x, r.dunnaAt.y, P.r + 700);
  let k = 0;
  if (h) {
    const x = h.x + rngRange(s.rng, -350, 350);
    const y = h.y + rngRange(s.rng, -350, 350);
    if (clear(x, y)) {
      add(x, y);
      k++;
    }
  }
  for (let guard = 0; k < P.n && guard < 20; guard++) {
    const c = cellCenter(s.map.radius, r.floor[rngInt(s.rng, r.floor.length)] as number);
    if (!clear(c.x, c.y) || w.spots.some((o) => o.t === t && withinRange(o.x, o.y, c.x, c.y, P.r * 2))) continue;
    add(c.x, c.y);
    k++;
  }
  w.pulses++;
  cue(`pulse${w.pulses}`);
};

/** A band of nightmares out of a tunnel, or off the river by Dunna (now and then); an ooze among them now and then. */
const foes = (s: BattleState, r: EmberRules, w: EmberState): void => {
  if (s.waveT < w.foe || w.end >= 0) return;
  w.foe = s.waveT + r.foes.every;
  const river = rngInt(s.rng, 10000) < r.foes.riverBp;
  const from = river ? r.spawns : r.tunnels;
  const at = from[rngInt(s.rng, from.length)] as { x: number; y: number };
  for (let k = 0; k < r.foes.n; k++) {
    const kind = (w.foes + k) % 5 === 4 ? 'slime' : 'wraith';
    const e = spawnEnemy(s, kind, 1, false, 0, at.x + rngRange(s.rng, -300, 300), at.y + rngRange(s.rng, -200, 300));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
  w.foes += r.foes.n;
  ev(s, { t: 'senda', kind: 'holeOut', npc: '', x: at.x, y: at.y, n: r.foes.n });
};

export const emberTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.ember;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.ember ??= fresh(r));
  if (!s.started) return;
  pulse(s, r, w, cue);
  pulseLand(s, w.spots, r.pulse);
  foes(s, r, w);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (!d || w.end >= 0) return;
  if (d.flinch > 0) d.flinch--;
  else if (++w.prog >= r.work) {
    w.end = s.tick;
    ev(s, { t: 'senda', kind: 'free', npc: d.id, x: d.x, y: d.y });
    cue('free');
  } else {
    if (w.prog * 2 >= r.work) cue('half');
    if (w.prog * 20 >= r.work * 17) cue('near');
    if (w.prog % 24 === 0) ev(s, { t: 'senda', kind: 'pry', npc: d.id, x: d.x, y: d.y, n: w.prog });
  }
  w.shot = shootTick(s, d, r.dunna, w.shot);
};

/** The hammer's freeing, out of its ticks. */
export const emberGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.ember;
  const w = s.senda?.ember;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.prog, need: r.work };
};

/** Won a moment after the hammer is free. */
export const emberResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.ember;
  if (!w || w.end < 0) return null;
  return s.tick >= w.end + 30 ? 'won' : null;
};
